import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import InputAdornment from '@mui/material/InputAdornment'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import { DateField } from '@mui/x-date-pickers/DateField'
import { addDays, endOfDay, endOfWeek, isAfter, isEqual, startOfDay, startOfWeek, subDays } from 'date-fns'
import { getDateFormat, getDateLocale } from 'global/helpers/dates'
import React, { type ReactElement, useState } from 'react'
import { type DateRange, DayPicker, rangeIncludesDate } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

const StyledDayPicker = styled(DayPicker)(({ theme }) => ({
  position: 'relative',
  '.rdp-months': {
    display: 'flex',
    gap: '1rem',
  },
  '.rdp-month_caption': {
    ...theme.typography['p4'],
    color: theme.palette.primary.main,
    lineHeight: '2rem',
  },
  '.rdp-nav': {
    position: 'absolute',
    top: 0,
    right: '-0.5rem',
  },
  '.rdp-button_previous, .rdp-button_next': {
    appearance: 'none',
    background: 'none',
    border: 'none',
    verticalAlign: 'top',
    cursor: 'pointer',
    width: '2rem',
    height: '2rem',
    '.rdp-chevron': {
      width: '1rem',
      height: '1rem',
      verticalAlign: 'top',
      fill: theme.palette.primary.light,
    },
    '&:hover .rdp-chevron': {
      fill: theme.palette.primary.main,
    },
    '&[disabled]': {
      visibility: 'hidden',
    },
  },
  '.rdp-month_grid': {
    margin: '0 auto',
    borderCollapse: 'collapse',
  },
  '.rdp-weekdays': {
    height: '2rem',
  },
  '.rdp-weekday': {
    ...theme.typography['p6'],
    color: theme.palette.primary.light,
  },
  '.rdp-day': {
    position: 'relative',
    ...theme.typography['p5'],
    '.rdp-day_button': {
      position: 'relative',
      display: 'block',
      appearance: 'none',
      border: 'none',
      background: 'none',
      color: 'inherit',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      width: '2.5rem',
      height: '2rem',
      textAlign: 'center',
      cursor: 'pointer',
    },
    '&:before': {
      content: '""',
      position: 'absolute',
      top: '2px',
      right: 0,
      bottom: '2px',
      left: 0,
    },
    '&.rdp-today': {
      color: theme.palette.secondary.main,
      ...theme.typography['p6'],
    },
    '&.rdp-outside, &.rdp-disabled': {
      color: theme.palette.primary.light,
    },
    '&.rdp-range_start, &.rdp-range_middle, &.rdp-range_end': {
      ...theme.typography['p6'],
      '&:before': {
        background: theme.palette.secondary.lighter,
      },
    },
    '&.rdp-selected:not(.rdp-range_start):not(.rdp-range_middle):not(.rdp-range_end)': {
      color: theme.palette.common.white,
      ...theme.typography['p6'],
      '&:before': {
        background: theme.palette.secondary.main,
        borderRadius: theme.borders.borderRadius['lg'],
      },
    },
    '&.rdp-preview_start, &.rdp-preview_middle, &.rdp-preview_end': {
      color: theme.palette.common.white,
      ...theme.typography['p6'],
      '&:before': {
        background: theme.palette.secondary.main,
      },
    },
    '&.rdp-range_start, &.rdp-preview_start, &.rdp-preview_end + .rdp-range_middle, &.rdp-preview_end + .rdp-range_end': {
      '&:before': {
        borderTopLeftRadius: theme.borders.borderRadius['lg'],
        borderBottomLeftRadius: theme.borders.borderRadius['lg'],
      },
    },
    '&.rdp-range_end, &.rdp-preview_end, &.rdp-range_middle:has(+.rdp-preview_start)': {
      '&:before': {
        borderTopRightRadius: theme.borders.borderRadius['lg'],
        borderBottomRightRadius: theme.borders.borderRadius['lg'],
      },
    },
    '&.rdp-range_start.rdp-preview_middle, &.rdp-range_end.rdp-preview_middle': {
      '&:before': {
        borderRadius: 0,
      },
    },
    '&.rdp-range_start.rdp-preview_end': {
      '&:before': {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      },
    },
    '&.rdp-range_end.rdp-preview_start': {
      '&:before': {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
    },
    '&.rdp-preview_start.rdp-preview_end': {
      '&:before': {
        borderRadius: theme.borders.borderRadius['lg'],
      },
    },
  },
}))

interface KNDatePickerBaseProps {
  minDate: Date
  maxDate: Date
  disabled?: boolean
  error?: boolean
}

interface KNDatePickerRangeProps extends KNDatePickerBaseProps {
  mode: 'range'
  value?: DateRange
  label?: never
  labelFrom?: string
  labelTo?: string
  onChange: (value?: DateRange) => void
}

interface KNDatePickerSingleProps extends KNDatePickerBaseProps {
  mode: 'single'
  value?: Date
  label?: string
  labelFrom?: never
  labelTo?: never
  onChange: (value?: Date) => void
}

type KNDatePickerProps = KNDatePickerRangeProps | KNDatePickerSingleProps

const dateFieldEndAdornment = (
  <InputAdornment position="end">
    <CalendarMonthIcon sx={{ color: 'primary.light' }} />
  </InputAdornment>
)

const normalizeDateRangeBoundaries = (value?: DateRange): DateRange | undefined => {
  if (!value) {
    return undefined
  }
  return {
    from: value.from ? startOfDay(value.from) : undefined,
    to: value.to ? endOfDay(value.to) : undefined,
  }
}

const KNDatePickerNext = ({
  mode,
  value,
  minDate,
  maxDate,
  label,
  labelFrom,
  labelTo,
  disabled,
  error,
  onChange,
}: KNDatePickerProps): ReactElement => {
  const { t } = useTranslation()

  const today = new Date()
  const lastWeek: DateRange = { from: subDays(today, 6), to: today }
  const currentWeek: DateRange = { from: startOfWeek(today, { locale: getDateLocale() }), to: endOfWeek(today, { locale: getDateLocale() }) }
  const nextWeek: DateRange = { from: today, to: addDays(today, 6) }
  const normalizedRangeValue = mode === 'range' ? normalizeDateRangeBoundaries(value) : undefined

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [month, setMonth] = useState(today)
  const [preview, setPreview] = useState<DateRange>()

  const handleFieldClick = (event: React.MouseEvent<HTMLElement>) => {
    setPreview(undefined)
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setPreview(undefined)
  }

  const handleSingleChange = (newValue?: Date) => {
    if (mode === 'single') {
      onChange(newValue)
    }
  }

  const handleRangeChange = (newValue?: DateRange) => {
    if (mode === 'range') {
      onChange(normalizeDateRangeBoundaries(newValue))
    }
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <Stack spacing={2} direction="row">
        {mode === 'single' && (
          <DateField
            fullWidth
            value={value ?? null}
            minDate={minDate}
            maxDate={maxDate}
            disabled={disabled}
            onChange={(newValue, context) => {
              if (!context.validationError) {
                handleSingleChange(newValue ?? undefined)
              }
            }}
            label={label}
            slotProps={{
              textField: { endAdornment: dateFieldEndAdornment, error: error },
            }}
            onClick={handleFieldClick}
            format={getDateFormat('short')}
          />
        )}
        {mode === 'range' && (
          <>
            <DateField
              fullWidth
              value={normalizedRangeValue?.from ?? null}
              minDate={minDate}
              maxDate={maxDate}
              onChange={(newValue, context) => {
                if (!context.validationError) {
                  handleRangeChange({
                    from: newValue ?? undefined,
                    to: normalizedRangeValue?.to,
                  })
                }
              }}
              label={labelFrom}
              slotProps={{
                textField: { endAdornment: dateFieldEndAdornment },
              }}
              onClick={handleFieldClick}
              format={getDateFormat('short')}
            />
            <DateField
              fullWidth
              value={normalizedRangeValue?.to ?? null}
              minDate={minDate}
              maxDate={maxDate}
              onChange={(newValue, context) => {
                if (!context.validationError) {
                  handleRangeChange({
                    from: normalizedRangeValue?.from,
                    to: newValue ?? undefined,
                  })
                }
              }}
              label={labelTo}
              slotProps={{
                textField: { endAdornment: dateFieldEndAdornment },
              }}
              onClick={handleFieldClick}
              format={getDateFormat('short')}
            />
          </>
        )}
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disableAutoFocus
        sx={{
          marginTop: 0.5,
        }}
      >
        <Box sx={{ padding: 2, paddingTop: 1 }}>
          {mode === 'single' && (
            <StyledDayPicker
              locale={getDateLocale()}
              mode="single"
              month={month}
              onMonthChange={setMonth}
              selected={value}
              onSelect={(day) => {
                handleSingleChange(day ?? undefined)
                handleClose()
              }}
              startMonth={minDate ?? undefined}
              endMonth={maxDate ?? undefined}
              disabled={disabled}
              footer={(
                <Stack direction="row" spacing={1} sx={{ marginTop: 2 }}>
                  <Button
                    color="secondary"
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      handleSingleChange(today)
                      handleClose()
                    }}
                    onMouseOver={() => setMonth(today)}
                    sx={{
                      paddingY: 0,
                      paddingX: 1,
                    }}
                  >
                    {t('form.today')}
                  </Button>
                </Stack>
              )}
            />
          )}
          {mode === 'range' && (
            <StyledDayPicker
              locale={getDateLocale()}
              mode="range"
              month={month}
              onMonthChange={setMonth}
              selected={normalizedRangeValue}
              onSelect={handleRangeChange}
              startMonth={minDate ?? undefined}
              endMonth={maxDate ?? undefined}
              disabled={disabled}
              modifiers={{
                selected: normalizedRangeValue,
                preview_start: preview?.from,
                preview_middle: (date: Date) => preview ? rangeIncludesDate(preview, date, true) : false,
                preview_end: preview?.to,
              }}
              modifiersClassNames={{
                preview_start: 'rdp-preview_start',
                preview_middle: 'rdp-preview_middle',
                preview_end: 'rdp-preview_end',
              }}
              onDayMouseEnter={(day: Date) => {
                if (!normalizedRangeValue) {
                  return
                }
                if (normalizedRangeValue.from && normalizedRangeValue.to) {
                  if (isEqual(day, normalizedRangeValue.from) || isAfter(day, normalizedRangeValue.from)) {
                    setPreview({ from: normalizedRangeValue.from, to: day })
                  } else {
                    setPreview({ from: day, to: normalizedRangeValue.to })
                  }
                } else {
                  setPreview({ from: day, to: day })
                }
              }}
              onDayMouseLeave={() => {
                setPreview(undefined)
              }}
              onDayClick={() => {
                if (!normalizedRangeValue) {
                  return
                }
                if (normalizedRangeValue.from && normalizedRangeValue.to) {
                  handleClose()
                }
              }}
              footer={(
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ marginTop: 2 }}>
                  <Button
                    color="secondary"
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      handleRangeChange(lastWeek)
                      handleClose()
                    }}
                    onMouseOver={() => {
                      setMonth(today)
                      setPreview(lastWeek)
                    }}
                    onMouseOut={() => {
                      setPreview(undefined)
                    }}
                    sx={{
                      paddingY: 0,
                      paddingX: 1,
                    }}
                  >
                    {t('form.last_7_days')}
                  </Button>
                  <Button
                    color="secondary"
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      handleRangeChange(currentWeek)
                      handleClose()
                    }}
                    onMouseOver={() => {
                      setMonth(today)
                      setPreview(currentWeek)
                    }}
                    onMouseOut={() => {
                      setPreview(undefined)
                    }}
                    sx={{
                      paddingY: 0,
                      paddingX: 1,
                    }}
                  >
                    {t('form.current_week')}
                  </Button>
                  <Button
                    color="secondary"
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      handleRangeChange(nextWeek)
                      handleClose()
                    }}
                    onMouseOver={() => {
                      setMonth(today)
                      setPreview(nextWeek)
                    }}
                    onMouseOut={() => {
                      setPreview(undefined)
                    }}
                    sx={{
                      paddingY: 0,
                      paddingX: 1,
                    }}
                  >
                    {t('form.next_7_days')}
                  </Button>
                </Stack>
              )}
            />
          )}
        </Box>
      </Popover>
    </>
  )
}

export default KNDatePickerNext
